import tenantConfig from 'config/tenant'
import { darken, lighten } from 'config/tenant/generateTheme'

export type StatusStyle = { background: string; color: string; dot?: string }

// Brand anchors come straight from the active tenant's seed (the same source
// applyTheme feeds to CSS). Rebranding is a one-place change: edit the seed in
// config/tenant/tenants/<tenant>.ts and every brand colour below follows.
//   • primary → navy family (#0E1A2B on the default tenant)
//   • accent  → gold family (#C8A35A on the default tenant)
const PRIMARY = tenantConfig.theme.emailPrimaryHex.toLowerCase()
const ACCENT = tenantConfig.theme.emailSecondaryHex.toLowerCase()

// Base brand palette — single source for one-off accent/count colors so we
// never hardcode hex at call sites. The navy/gold shades derive from the seed
// (brand, unchanged). The block below is the vibrant SEMANTIC status palette —
// bright, high-contrast, premium hues inspired by Linear / Stripe / Radix, one
// distinct hue per status meaning so every badge is identifiable at a glance.
export const BRAND = {
  gold: ACCENT,
  goldLight: lighten(ACCENT, 14),
  goldWarm: lighten(ACCENT, 11),
  bronze: darken(ACCENT, 14),
  navy: PRIMARY,
  navyMid: lighten(PRIMARY, 22),
  navyDeep: darken(PRIMARY, 4),
  // The tenant's interactive primary — dashboard banner, primary buttons,
  // toggles, sidebar icons. Mirrors the --button-background-color-primary
  // applyTheme.ts writes at runtime (tenantConfig.buttonPrimaryHex); this is
  // the JS-side handle for the same colour (charts, inline styles).
  royal: tenantConfig.buttonPrimaryHex.toLowerCase(),
  royalLight: lighten(tenantConfig.buttonPrimaryHex, 26),
  // Palette extras — cream soft-accent surface + warm taupe. The cream is the
  // site's --color-gold-glow (#c8a35a26) composited on white.
  accentSoft: '#f7f1e6',
  warm: '#887456',
  steel: '#6b83a3',
  steelLight: '#8a9bb5',
  // ── Vibrant semantic status palette ────────────────────────────────────────
  green: '#16a34a', //     live / received / published  — money landed, running
  emerald: '#10b981', //   active / available           — open, live now
  greenDeep: '#047857', // paid / completed             — terminal success
  teal: '#0d9488', //      refunded                     — money reversal
  sky: '#0284c7', //       in progress                  — work in motion
  blue: '#2563eb', //      approved                     — confirmed / signed off
  indigo: '#6366f1', //    booked                       — firm ownership
  violet: '#7c3aed', //    reserved                     — soft hold
  amber: '#f59e0b', //     partial / reversed           — waiting (attention)
  amberSoft: '#fbbf24', // pending / pending-approval   — waiting (soft)
  orange: '#ea580c', //    due / on hold                — action needed now
  red: '#dc2626', //       overdue / blocked / bounced  — problem, intervene
  redBright: '#dc2626', // (kept for existing consumers; same red)
  crimson: '#e11d48', //   cancelled / rejected         — terminal failure
  slate: '#64748b', //     waived                       — special neutral
  grey: '#94a3b8', //      draft / archived             — inert / dormant
}

// Neutral UI palette (non-status greys / borders / surfaces). The JS mirror of
// the neutral SCSS tokens in _variables.scss, so inline `.tsx` styles reference a
// named handle instead of hardcoding a loose hex. Every value is VERBATIM the
// literal it replaces (zero visual change) — keep in sync with the SCSS token
// noted in each comment.
export const UI = {
  white: '#ffffff',
  // text
  textGray: '#6b7280', //      gray-500   ($select-arrow-color)
  textGray700: '#374151', //   gray-700
  textSlate: '#475569', //     slate-600  ($color-text-muted / CHART_NEUTRAL.text)
  textSlate800: '#1f2937', //  ($text-color-slate)
  placeholder: '#9ca3af', //   gray-400   ($text-color-placeholder)
  ink: '#0f172a', //           slate-900  ($tooltip-bg-base)
  // borders
  border: '#e5e7eb', //        gray-200   ($border-color-soft-200)
  borderSlate: '#e2e8f0', //   slate-200  ($border-color-soft)
  borderDashed: '#cbd5e1', //  slate-300  ($border-color-dashed)
  cancelBg: '#f0f0f0', //      ($color-cancel-bg)
  // surfaces
  surfaceSlate50: '#f8fafc', //   ($surface-slate-50)
  surfaceSlate100: '#f1f5f9', //  ($surface-slate-100)
  surfaceGray50: '#f9fafb', //    ($surface-gray-50)
  dropzoneActive: '#fffaf2', //   ($dropzone-active-bg)
  canvasDark: '#0b1220', //       DwgPreview dark canvas bg
  // status-ish soft fills used in inline badges
  danger: '#ef4444', //        red-500    ($color-kpi-red) — distinct from BRAND.red #dc2626
  dangerSoft: '#fef2f2', //    ($surface-danger-soft)
  dangerBorder: '#fecaca', //  red-200
  dangerText: '#991b1b', //    red-800
  infoSoft: '#eff6ff', //      blue-50    ($role-customer-bg)
  infoBorder: '#bfdbfe', //    blue-200
  warnSoft: '#fffbeb', //      amber-50
  warnBorder: '#fcd34d', //    amber-300
} as const

// Distinct on-brand sequence for generic chart breakdowns (no status meaning).
export const CHART_SEQUENCE = [
  BRAND.gold, BRAND.navy, BRAND.goldLight, BRAND.red,
  BRAND.steel, BRAND.navyDeep, BRAND.bronze, BRAND.steelLight,
  BRAND.goldWarm, BRAND.green,
]

// ─── Semantic status tokens ─────────────────────────────────────────────────────
// Every status map below points at one of these meanings instead of picking its
// own background/foreground pair. A status means the same thing — and therefore
// looks the same — in every module. Each token owns a distinct vibrant hue so
// different meanings never share a shade; related states (the three greens for
// success, the reds for failure/problem) stay grouped but visibly stepped.
//
//   live / active / done → success family (green → emerald → deep emerald)
//   approved / progress / booked / reserved → in-motion family (blue/sky/indigo/violet)
//   waiting → amber (pending) / orange (due, needs action now)
//   problem → red   (overdue / blocked / bounced — intervene)
//   failed  → crimson (cancelled / rejected — terminal)
//   off     → red   (inactive — switched off, meant to be spotted)
//   inert   → grey  (draft / archived — unfinished / filed away)
//
// Each token is a soft "quiet badge" pair (Linear / Stripe / GitHub style): a
// light tinted fill + the SAME hue as deep, high-contrast text. Reads premium
// and stays easy on the eye in dense tables, while the hue still identifies the
// status at a glance. The vivid BRAND hues above still drive charts, the plot
// map and progress bars — only the badge fills are softened here.
type Style = { background: string; color: string }

const LIVE: Style = { background: '#dcfce7', color: '#15803d' } //     green   — received / published
const ACTIVE_LIVE: Style = { background: '#dcfce7', color: '#16a34a' } // green — active / live (bright green, distinct from the deep-emerald COMPLETED)
const DONE_OK: Style = { background: '#d1fae5', color: '#065f46' } //   deep emerald — paid / completed
const SETTLED: Style = { background: '#dbeafe', color: '#1d4ed8' } //   blue    — approved
const PROGRESS_STYLE: Style = { background: '#e0f2fe', color: '#0369a1' } // sky  — in progress
const BOOKED_STYLE: Style = { background: '#e0e7ff', color: '#4338ca' } // indigo — booked
const RESERVED_STYLE: Style = { background: '#ede9fe', color: '#6d28d9' } // violet — reserved
const REFUND_STYLE: Style = { background: '#ccfbf1', color: '#0f766e' } // teal   — refunded
const WAITING: Style = { background: '#fef3c7', color: '#b45309' } //    amber   — partial / reversed
const WAITING_SOFT: Style = { background: '#fef9c3', color: '#a16207' } // yellow — pending / pending-approval
const DUE_STYLE: Style = { background: '#ffedd5', color: '#c2410c' } //  orange  — due / on hold
const PROBLEM: Style = { background: '#fee2e2', color: '#b91c1c' } //    red     — blocked / blacklisted / bounced
const OVERDUE_WARN: Style = { background: '#fee2e2', color: '#b91c1c' } // red    — overdue
const FAILED: Style = { background: '#ffe4e6', color: '#be123c' } //     rose    — cancelled / rejected
const INERT: Style = { background: '#f1f5f9', color: '#475569' } //      slate   — draft / archived

// Inactive reads as red across every module — a switched-off record is meant to
// be spotted at a glance, not to fade into the table. Its own token rather than
// a reuse of PROBLEM: brokers show INACTIVE and BLACKLISTED side by side, so the
// lighter fill + brighter red keeps "dormant" a visible step below "blacklisted"
// instead of rendering the two identically. Deliberately NOT folded into INERT —
// that token still greys DRAFT and ARCHIVED, which are unfinished/filed away,
// not switched off.
const INACTIVE_RED: Style = { background: '#fef2f2', color: '#dc2626' } // red  — inactive / switched off

/** Same style, plus a leading dot rendered in the badge's own (deep) hue. */
const dotted = (s: Style): StatusStyle => ({ ...s, dot: s.color })

// ─── RSuite named colour → soft badge ────────────────────────────────────────
// Bridge for the badges still rendered with an RSuite named colour
// (`<Tag color="green">`). `softTag(name)` returns the matching soft tint so
// those sites can render the same premium pill as StatusCell — just swap
// `color={x}` for `style={softTag(x)}`, no markup change.
export const NAMED_SOFT: Record<string, StatusStyle> = {
  green: { background: '#dcfce7', color: '#15803d', dot: '#15803d' },
  red: { background: '#fee2e2', color: '#b91c1c', dot: '#b91c1c' },
  orange: { background: '#ffedd5', color: '#c2410c', dot: '#c2410c' },
  yellow: { background: '#fef9c3', color: '#a16207', dot: '#a16207' },
  cyan: { background: '#e0f2fe', color: '#0369a1', dot: '#0369a1' },
  blue: { background: '#dbeafe', color: '#1d4ed8', dot: '#1d4ed8' },
  violet: { background: '#ede9fe', color: '#6d28d9', dot: '#6d28d9' },
}

/**
 * Soft badge style for an RSuite colour name. Returns `{ background, color }`
 * only, so it is safe to spread straight into a Tag's `style`. Unknown or
 * missing names fall back to the neutral inert grey.
 */
export const softTag = (named?: string | null): { background: string; color: string } => {
  const s = NAMED_SOFT[named ?? ''] ?? INERT
  return { background: s.background, color: s.color }
}

/**
 * Drop a status token straight into a Tag's `style`. Strips `dot` — it belongs
 * to the badge's leading dot, not to CSS, and React warns on unknown properties.
 */
export const tagStyle = (s: StatusStyle): { background: string; color: string } =>
  ({ background: s.background, color: s.color })

// Comprehensive status → style. Used anywhere a mixed set of statuses is shown
// (e.g. the Reports table).
export const GENERIC_STATUS_STYLE: Record<string, StatusStyle> = {
  ACTIVE: dotted(ACTIVE_LIVE),
  // Booking's ACTIVE displayed as "Ongoing" (see BOOKING_STATUS_DISPLAY). Charts
  // colour their slices by the DISPLAYED name, so the alias keeps the Ongoing
  // slice on the same green as the ACTIVE badge it stands for.
  ONGOING: dotted(ACTIVE_LIVE),
  COMPLETED: dotted(DONE_OK),
  RECEIVED: dotted(LIVE),
  PAID: dotted(DONE_OK),
  APPROVED: dotted(SETTLED),
  RESERVED: dotted(RESERVED_STYLE),
  IN_PROGRESS: dotted(PROGRESS_STYLE),
  BOOKED: dotted(BOOKED_STYLE),
  PARTIAL: dotted(WAITING),
  ON_HOLD: dotted(DUE_STYLE),
  PENDING_APPROVAL: dotted(WAITING_SOFT),
  PENDING: dotted(WAITING_SOFT),
  DRAFT: dotted(INERT),
  OVERDUE: dotted(OVERDUE_WARN),
  DUE: dotted(DUE_STYLE),
  CANCELLED: dotted(FAILED),
  REJECTED: dotted(FAILED),
  BLOCKED: dotted(PROBLEM),
  BLACKLISTED: dotted(PROBLEM),
  BOUNCED: dotted(PROBLEM),
  AVAILABLE: dotted(LIVE),
  UNAVAILABLE: dotted(INERT),
  INACTIVE: dotted(INACTIVE_RED),
}

// ─── Plot status ────────────────────────────────────────────────────────────────
// Plot-map scheme (self-contained; NOT the status-badge hues): Available = green
// (open, ready to sell), Reserved = blue (held), Booked = red (taken / closed),
// Unavailable = neutral grey (off the market — no sale in play at all).
// Sold red-family, Blocked slate, On-hold sky — kept distinct so the canvas stays
// legible. UNAVAILABLE takes the NEUTRAL grey family (#6b7280) rather than
// BLOCKED's slate-blue, so the two read apart where both appear.
export const PLOT_STATUS_COLOR: Record<string, string> = {
  AVAILABLE: "#22c55e",
  BOOKED: "#ef4444",
  RESERVED: "#3b82f6",
  UNAVAILABLE: "#6b7280",
  SOLD: BRAND.red,
  BLOCKED: BRAND.slate,
  ON_HOLD: "#0ea5e9",
}
export const PLOT_STATUS_BADGE_STYLE: Record<string, StatusStyle> = {
  ...GENERIC_STATUS_STYLE,
  AVAILABLE: dotted(LIVE),
  RESERVED: dotted(SETTLED),
  BOOKED: dotted(PROBLEM),
  UNAVAILABLE: dotted(INERT),
}

export const PLOT_FILL: Record<string, string> = {
  AVAILABLE: "#22c55e",
  RESERVED: "#3b82f6",
  BOOKED: "#ef4444",
  UNAVAILABLE: "#6b7280",
  SOLD: "#fee2e2",
  BLOCKED: "#e2e8f0",
  ON_HOLD: "#e0f2fe",
}

export const PLOT_FILL_HOVER: Record<string, string> = {
  AVAILABLE: "#16a34a",
  RESERVED: "#2563eb",
  BOOKED: "#dc2626",
  UNAVAILABLE: "#4b5563",
  SOLD: "#fecaca",
  BLOCKED: "#cbd5e1",
  ON_HOLD: "#bae6fd",
}

export const PLOT_STROKE: Record<string, string> = {
  AVAILABLE: "#16a34a",
  RESERVED: "#2563eb",
  BOOKED: "#dc2626",
  UNAVAILABLE: "#4b5563",
  SOLD: "#f87171",
  BLOCKED: "#94a3b8",
  ON_HOLD: "#7dd3fc",
}

export const PLOT_TEXT: Record<string, string> = {
  AVAILABLE: "#ffffff",
  RESERVED: "#ffffff",
  BOOKED: "#ffffff",
  UNAVAILABLE: "#ffffff",
  SOLD: "#991b1b",
  BLOCKED: "#334155",
  ON_HOLD: "#075985",
}

// RSuite's Tag palette has no grey, so UNAVAILABLE borrows the nearest inert
// tone it does offer. The soft badge (GENERIC_STATUS_STYLE) carries the real
// grey wherever the styled pill is used instead of a named Tag.
export const PLOT_STATUS_TAG: Record<string, any> = {
  AVAILABLE: "green",
  RESERVED: "blue",
  BOOKED: "red",
  UNAVAILABLE: "red",
  SOLD: "red",
  BLOCKED: "red",
  ON_HOLD: "cyan",
}

export const LEGEND_META: Record<string, { dot: string; label: string }> = {
  AVAILABLE: { dot: "#22c55e", label: "Available" },
  RESERVED: { dot: "#3b82f6", label: "Reserved" },
  BOOKED: { dot: "#ef4444", label: "Booked" },
  UNAVAILABLE: { dot: "#6b7280", label: "Unavailable" },
  SOLD: { dot: BRAND.red, label: "Sold" },
  BLOCKED: { dot: BRAND.slate, label: "Blocked" },
  ON_HOLD: { dot: "#0ea5e9", label: "On Hold" },
}

// ─── Booking status ─────────────────────────────────────────────────────────────
export const BOOKING_STATUS_STYLE: Record<string, StatusStyle> = {
  PENDING_APPROVAL: WAITING_SOFT,
  APPROVED: SETTLED,
  ACTIVE: ACTIVE_LIVE,
  COMPLETED: DONE_OK,
  CANCELLED: FAILED,
}

export const BOOKING_STATUS_CELL_STYLE: Record<string, StatusStyle> = {
  PENDING_APPROVAL: dotted(WAITING_SOFT),
  APPROVED: dotted(SETTLED),
  ACTIVE: dotted(ACTIVE_LIVE),
  COMPLETED: dotted(DONE_OK),
  CANCELLED: dotted(FAILED),
}

// RSuite's own Tag palette, so these can only approximate the semantic tokens
// above — it has no navy/indigo/teal. Map to the nearest meaning: live/success
// states green, approved blue, waiting orange, failures red.
export const BOOKING_STATUS_TAG_COLOR: Record<string, 'orange' | 'blue' | 'green' | 'cyan' | 'red'> = {
  PENDING_APPROVAL: 'orange',
  APPROVED: 'blue',
  ACTIVE: 'green',
  COMPLETED: 'green',
  CANCELLED: 'red',
}

export const BOOKING_DETAIL_STATUS_TAG_COLOR: Partial<Record<string, 'red' | 'orange' | 'yellow' | 'green' | 'cyan' | 'blue' | 'violet'>> = {
  COMPLETED: 'green',
  PENDING_APPROVAL: 'yellow',
  CANCELLED: 'red',
}

// ─── Booking status labels ──────────────────────────────────────────────────
// A booking's ACTIVE status READS as "Ongoing" — display only. The stored
// value, the API payloads, the list filters and every BOOKING_STATUS_* colour
// map above stay keyed by ACTIVE, so nothing but the text changes. The rename
// is booking-only: Customer / Project / Broker / User keep their own "Active".
const BOOKING_STATUS_DISPLAY: Record<string, string> = {
  ACTIVE: 'ONGOING',
}

/**
 * Display text for a booking status. `format` is whatever transform the screen
 * already applied to the raw status (identity, underscore-stripping, title
 * case) — it runs on the renamed token, so ONGOING picks up the same casing as
 * the statuses beside it and no other status's text changes.
 */
export const bookingStatusLabel = (
  status: string,
  format: (s: string) => string = (s) => s
): string => format(BOOKING_STATUS_DISPLAY[status] ?? status)

/** Booking status → title-case label, for a StatusCell / tab `labelMap`. */
export const BOOKING_STATUS_LABEL_MAP: Record<string, string> = {
  PENDING_APPROVAL: 'Pending',
  APPROVED: 'Approved',
  ACTIVE: 'Ongoing',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

// The booking lifecycle, in order. Anywhere the statuses are LISTED — list tabs,
// chart legends, KPI strips — they follow this sequence rather than whatever
// order the API happened to return, so the same story reads left to right on
// every screen: awaiting approval → approved → ongoing → completed → cancelled.
export const BOOKING_STATUS_ORDER = [
  'PENDING_APPROVAL',
  'APPROVED',
  'ACTIVE',
  'COMPLETED',
  'CANCELLED',
]

/**
 * Sort any booking-status-keyed list into the lifecycle sequence. Statuses the
 * order doesn't know about (a new one added server-side) keep their relative
 * order and sit at the end rather than being dropped.
 */
export const sortByBookingStatus = <T>(
  items: T[],
  statusOf: (item: T) => string
): T[] => {
  const rank = (s: string) => {
    const i = BOOKING_STATUS_ORDER.indexOf(s)
    return i === -1 ? BOOKING_STATUS_ORDER.length : i
  }
  return [...items].sort((a, b) => rank(statusOf(a)) - rank(statusOf(b)))
}

// ─── Project status ─────────────────────────────────────────────────────────────
// ARCHIVED is inert, not a failure — grey. DRAFT is unfinished, also grey.
export const PROJECT_STATUS_STYLE: Record<string, StatusStyle> = {
  DRAFT: dotted(INERT),
  ACTIVE: dotted(ACTIVE_LIVE),
  ON_HOLD: dotted(DUE_STYLE),
  COMPLETED: dotted(DONE_OK),
  ARCHIVED: dotted(INERT),
}

// ─── Layout publish status ──────────────────────────────────────────────────────
// A published layout is live (green); an unpublished draft is inert grey.
export const LAYOUT_STATUS_STYLE: Record<string, StatusStyle> = {
  PUBLISHED: LIVE,
  DRAFT: INERT,
}

// Neutral fallbacks for charts (unknown status fills / tooltip borders).
export const CHART_NEUTRAL = {
  fill: '#e5e7eb',
  stroke: '#d1d5db',
  text: '#475569',
  border: '#eaecf0',
}

// ─── Customer status ────────────────────────────────────────────────────────────
export const CUSTOMER_STATUS_STYLE: Record<string, StatusStyle> = {
  ACTIVE: dotted(ACTIVE_LIVE),
  INACTIVE: dotted(INACTIVE_RED),
}

// ─── Project access (invited user ↔ selected project) ───────────────────────────
// The invite list reports access per project through a `revoked` flag, which is
// the inverse of the badge: `revoked: false` (or absent) means the user still
// holds the project. Granted rides the same green as any other live record;
// revoked rides the INACTIVE red, so a withdrawn user is spotted at a glance.
export const PROJECT_ACCESS_STYLE: Record<string, StatusStyle> = {
  GRANTED: dotted(ACTIVE_LIVE),
  REVOKED: dotted(INACTIVE_RED),
}

export const PROJECT_ACCESS_LABEL_MAP: Record<string, string> = {
  GRANTED: 'Granted',
  REVOKED: 'Revoked',
}

// ─── Payment & Schedule status ──────────────────────────────────────────────────
// WAIVED keeps its own slate tone: it is neither settled money nor a problem.
export const PAYMENT_STATUS_STYLE: Record<string, StatusStyle> = {
  RECEIVED: dotted(LIVE),
  BOUNCED: dotted(PROBLEM),
  REVERSED: dotted(WAITING),
  REFUNDED: dotted(REFUND_STYLE),
}

export const SCHEDULE_STATUS_STYLE: Record<string, StatusStyle> = {
  PENDING: dotted(WAITING_SOFT),
  PARTIAL: dotted(WAITING),
  PAID: dotted(DONE_OK),
  OVERDUE: dotted(OVERDUE_WARN),
  WAIVED: { background: '#e2e8f0', color: '#334155', dot: '#334155' },
}

// Combined map for unified payment list (schedule status first, then payment)
export const PAYMENT_SCHEDULE_STATUS_STYLE: Record<string, StatusStyle> = {
  ...SCHEDULE_STATUS_STYLE,
  ...PAYMENT_STATUS_STYLE,
}

export const PAYMENT_STATUS_LABEL_MAP: Record<string, string> = {
  RECEIVED: 'Received',
  BOUNCED: 'Bounced',
  REVERSED: 'Reversed',
  REFUNDED: 'Refunded',
  PENDING: 'Pending',
  PARTIAL: 'Partial',
  PAID: 'Paid',
  OVERDUE: 'Overdue',
  WAIVED: 'Waived',
}

// ─── Default fallback for StatusCell ────────────────────────────────────────────
export const DEFAULT_STATUS_STYLE: Record<string, StatusStyle> = {
  ACTIVE: dotted(ACTIVE_LIVE),
  INACTIVE: dotted(INACTIVE_RED),
}

// ─── Communication template purpose ─────────────────────────────────────────
// Categories, not semantics — so they ride the brand palette. Birthday/Festival
// deliberately match the calendar chips in `calendarPage.scss` (cream / gold).
export const TEMPLATE_PURPOSE_STYLE: Record<string, StatusStyle> = {
  SYSTEM: { background: BRAND.royal, color: '#ffffff', dot: BRAND.royal },
  BIRTHDAY: { background: BRAND.accentSoft, color: BRAND.navyDeep, dot: BRAND.gold },
  FESTIVAL: { background: BRAND.gold, color: '#ffffff', dot: BRAND.gold },
  PROJECT_UPDATE: { background: BRAND.steel, color: '#ffffff', dot: BRAND.steel },
  CUSTOM: { background: BRAND.warm, color: '#ffffff', dot: BRAND.warm },
}

// ─── Broker status ──────────────────────────────────────────────────────────────
// Both negatives are red; blacklisted stays the heavier one (deeper fill, darker
// text) so it still outranks a merely-inactive broker at a glance.
export const BROKER_STATUS_STYLE: Record<string, StatusStyle> = {
  ACTIVE: dotted(ACTIVE_LIVE),
  INACTIVE: dotted(INACTIVE_RED),
  BLACKLISTED: dotted(PROBLEM),
}

export const BROKER_STATUS_LABEL_MAP: Record<string, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  BLACKLISTED: 'Blacklisted',
}

// ─── Payment progress bar (PlotDetail) ──────────────────────────────────────
export const PAYMENT_PROGRESS_COLOR = {
  EMPTY: BRAND.red,
  PARTIAL: BRAND.goldLight,
  HALF: BRAND.goldWarm,
  FULL: BRAND.green,
} as const
